import React from 'react';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, ShieldCheckIcon } from 'lucide-react';
import { WhatsAppIcon } from './WhatsAppIcon';
interface FooterProps {
  onNavigate: (page: string) => void;
}
export function Footer({
  onNavigate
}: FooterProps) {
  const navLinks = [{
    id: 'home',
    label: 'Home'
  }, {
    id: 'hypotheken',
    label: 'Hypotheken'
  }, {
    id: 'woonboten',
    label: 'Woonboten'
  }, {
    id: 'verzekeringen',
    label: 'Verzekeringen'
  }, {
    id: 'over-ons',
    label: 'Over ons'
  }, {
    id: 'kosten',
    label: 'Kosten'
  }, {
    id: 'contact',
    label: 'Contact'
  }];
  const handleNavClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    onNavigate(id);
    window.scrollTo(0, 0);
  };
  return <footer className="bg-navy text-white pt-16 pb-24 md:pb-0" data-id="element-35">
      <div className="max-w-7xl mx-auto px-4 md:px-8 mb-12" data-id="element-36">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12" data-id="element-37">
          {/* Column 1: Brand */}
          <div data-id="element-38">
            <div className="bg-white p-4 rounded-brand inline-block mb-6" data-id="element-39">
              <img src="https://cdn.magicpatterns.com/uploads/iq7SCoyHFduQKkxbRqiiPv/JDG_logo.jpg" alt="JDG Hypotheken | Verzekeringen" className="h-10 object-contain" data-id="element-40" />
            </div>
            <p className="text-gray-300 text-lg leading-relaxed max-w-sm" data-id="element-41">
              Persoonlijk financieel advies dat past bij jouw situatie.
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div data-id="element-42">
            <h3 className="text-gold font-bold text-xl mb-6" data-id="element-43">Snel naar</h3>
            <ul className="space-y-3" data-id="element-44">
              {navLinks.map(link => <li key={link.id} data-id="element-45">
                  <a href={`#${link.id}`} onClick={e => handleNavClick(e, link.id)} className="text-gray-300 hover:text-gold transition-colors inline-block" data-id="element-46">
                    {link.label}
                  </a>
                </li>)}
            </ul>
          </div>

          {/* Column 3: Contact Info */}
          <div data-id="element-47">
            <h3 className="text-gold font-bold text-xl mb-6" data-id="element-48">Contact</h3>
            <ul className="space-y-4" data-id="element-49">
              <li className="flex items-start" data-id="element-50">
                <MapPinIcon className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" data-id="element-51" />
                <span className="text-gray-300" data-id="element-52">
                  Sloterweg 228
                  <br data-id="element-53" />
                  1171 CX Badhoevedorp
                </span>
              </li>
              <li className="flex items-center" data-id="element-54">
                <PhoneIcon className="w-5 h-5 text-gold mr-3 flex-shrink-0" data-id="element-55" />
                <a href="tel:0232052161" className="text-gray-300 hover:text-white transition-colors" data-id="element-56">
                  023-205 2161
                </a>
              </li>
              <li className="flex items-center" data-id="element-57">
                <MailIcon className="w-5 h-5 text-gold mr-3 flex-shrink-0" data-id="element-58" />
                <a href="mailto:info@jdgadvies.nl" className="text-gray-300 hover:text-white transition-colors" data-id="element-59">
                  info@jdgadvies.nl
                </a>
              </li>
              <li className="flex items-start pt-2" data-id="element-60">
                <ClockIcon className="w-5 h-5 text-gold mr-3 mt-1 flex-shrink-0" data-id="element-61" />
                <span className="text-gray-300" data-id="element-62">
                  Ma-Vr: 09:00 – 17:30
                  <br data-id="element-63" />
                  Za: Op afspraak
                </span>
              </li>
              <li className="flex items-center pt-2" data-id="element-64">
                <a href="https://wa.me/31612345678" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-white hover:text-gold transition-colors font-medium" data-id="element-65">
                  <WhatsAppIcon className="w-5 h-5 mr-2" data-id="element-66" />
                  Stuur een WhatsApp
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Keurmerken */}
          <div data-id="element-67">
            <h3 className="text-gold font-bold text-xl mb-6" data-id="element-68">
              Keurmerken & Registraties
            </h3>
            <ul className="space-y-4" data-id="element-69">
              <li className="flex items-center gap-4 bg-white/10 p-4 rounded-xl border border-white/10" data-id="element-70">
                <div className="bg-white rounded-lg p-2 flex-shrink-0" data-id="element-71">
                  <img src="https://cdn.magicpatterns.com/uploads/hLrKue75gCT8qjkamHbfdK/JDG_Erkend_adviseur.png" alt="Erkend Financieel Adviseur" className="h-7 w-auto object-contain" data-id="element-72" />
                </div>
                <span className="text-white text-sm font-semibold leading-tight" data-id="element-73">
                  Erkend Financieel Adviseur
                </span>
              </li>
              <li className="flex items-center" data-id="element-74">
                <ShieldCheckIcon className="w-5 h-5 text-gold mr-3 flex-shrink-0" data-id="element-75" />
                <span className="text-gray-300" data-id="element-76">AFM-geregistreerd</span>
              </li>
              <li className="flex items-center" data-id="element-77">
                <ShieldCheckIcon className="w-5 h-5 text-gold mr-3 flex-shrink-0" data-id="element-78" />
                <span className="text-gray-300" data-id="element-79">Aangesloten bij Kifid</span>
              </li>
              <li className="flex items-center" data-id="element-80">
                <ShieldCheckIcon className="w-5 h-5 text-gold mr-3 flex-shrink-0" data-id="element-81" />
                <span className="text-gray-300" data-id="element-82">
                  Beroepsaansprakelijkheidsverzekering
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Legal Strip */}
      <div className="bg-[#001f3f] py-6" data-id="element-83">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400 gap-6" data-id="element-84">
          <div className="flex flex-col gap-1 text-center md:text-left" data-id="element-85">
            <span data-id="element-86">
              &copy; {new Date().getFullYear()} JDG Hypotheken | Verzekeringen.
              Alle rechten voorbehouden.
            </span>
            <span className="text-gray-500" data-id="element-87">
              AFM vergunning: [in aanvraag] | Kifid aansluitnummer: [in
              aanvraag] | KVK: [in aanvraag]
            </span>
          </div>
          <div className="flex flex-col gap-3 items-center md:items-end" data-id="element-88">
            <div className="flex flex-wrap justify-center md:justify-end gap-4" data-id="element-89">
              <a href="#" className="hover:text-white transition-colors" data-id="element-90">
                Wat wij voor je doen
              </a>
              <span className="hidden md:inline text-gray-600" data-id="element-91">|</span>
              <a href="#" className="hover:text-white transition-colors" data-id="element-92">
                Vergelijkingskaart Hypotheek
              </a>
              <span className="hidden md:inline text-gray-600" data-id="element-93">|</span>
              <a href="#" className="hover:text-white transition-colors" data-id="element-94">
                Vergelijkingskaart Risico's afdekken
              </a>
            </div>
            <div className="flex flex-wrap justify-center md:justify-end gap-4" data-id="element-95">
              <a href="#" onClick={e => handleNavClick(e, 'kosten')} className="hover:text-white transition-colors" data-id="element-96">
                Kosten
              </a>
              <span className="hidden md:inline text-gray-600" data-id="element-97">|</span>
              <a href="#" className="hover:text-white transition-colors" data-id="element-98">
                Beloningsbeleid
              </a>
              <span className="hidden md:inline text-gray-600" data-id="element-99">|</span>
              <a href="#" className="hover:text-white transition-colors" data-id="element-100">
                Privacyverklaring
              </a>
              <span className="hidden md:inline text-gray-600" data-id="element-101">|</span>
              <a href="#" className="hover:text-white transition-colors" data-id="element-102">
                Klachtenprocedure
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}